import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';

/** The gateway's live health of a camera; `idle`: not streamed, health unknown. */
export type CameraStatus = 'live' | 'starting' | 'offline' | 'idle';
export interface Camera {
  id: string;
  channel: number;
  name: string;
  featured: boolean;
  status: CameraStatus;
  /** ISO UTC: the camera's last video (or first failure); only while offline. */
  offline_since?: string;
}
export interface CameraStore {
  id: string;
  name: string;
  /** The ERP store (location) the recorder serves, where the gateway config names it. */
  location_id?: string;
  cameras: Camera[];
}
/** One video of every featured camera (`live/mosaic/grid/`), in source pixels. */
export interface CameraMosaic {
  width: number;
  height: number;
  cols: number;
  rows: number;
  tile_width: number;
  tile_height: number;
  cells: { camera: string; x: number; y: number }[];
}
export interface CameraCatalog {
  stores: CameraStore[];
  configured?: boolean;
  buffer_seconds?: number;
  mosaic?: CameraMosaic;
}
export interface RecordingInterval {
  start: string;
  end: string;
}
/** One recorded stretch of an archive session: recorder-clock start, place in the stream. */
export interface ArchivePart {
  /** Device-local `YYYY-MM-DDTHH:MM:SS`. */
  start: string;
  /** Seconds of the stream before this part. */
  offset: number;
  duration: number;
}

export type ArchiveQuality = 'hd' | 'grid';

/**
 * A camera's recording from a chosen second as one growing HLS stream: the gateway
 * waits for the recorder (`queued`, more urgent cameras first), loads ahead of the
 * viewer (`loading`), rests with enough loaded or once no page watches it (`idle`)
 * and ends the stream when the recordings end (`ready`).
 */
export interface ArchiveSession {
  state: 'queued' | 'loading' | 'idle' | 'ready' | 'unavailable';
  requested_start: string;
  quality: ArchiveQuality;
  /** The stream lists a first segment: it plays while the rest loads. */
  playable: boolean;
  parts: ArchivePart[];
  loaded_until: string | null;
}
/**
 * How urgently a page needs a camera's archive: the camera looked at, the other tiles
 * on screen, tiles out of sight. The gateway serves each recorder in that order.
 */
export type ArchivePriority = 'focused' | 'visible' | 'offscreen';

/** One camera a page watches in the archive: its session from second `at`. */
export interface ArchiveWatch {
  camera: string;
  /** Recorder-clock `YYYYMMDDTHHMMSS` (see `archiveStamp`). */
  at: string;
  quality: ArchiveQuality;
  priority: ArchivePriority;
  /** Try a failed session again (the tile's retry button). */
  retry?: boolean;
}

/**
 * Everything a page watches in the archive: a newer `revision` replaces the page's
 * lease whole (a seek stops the earlier moments at once), the same one renews it, an
 * empty `watch` releases it. A page gone without a word lapses after a few seconds.
 */
export interface ArchiveLeaseRequest {
  revision: number;
  watch: ArchiveWatch[];
}

export interface ArchiveSessionStatus extends ArchiveSession {
  camera: string;
  at: string;
  priority: ArchivePriority;
}

export interface ArchiveLease {
  revision: number;
  sessions: ArchiveSessionStatus[];
}

export type PresenceResult = 'visible' | 'not_visible' | 'uncertain' | 'no_data';
export type PresenceReviewState = 'observation' | 'confirmed' | 'dismissed';
/** Apron state of a store minute with a person in a staff zone; null when not assessed. */
export type ApronState = 'worn' | 'missing' | 'uncertain';
/**
 * `absence`: staff not visible; `apron`: staff seen without the apron; `after_hours`:
 * people in the closed store; `extra_person`: more people behind the counter than the
 * store's staff. A newer server may send a kind this page does not know yet: it must
 * render neutrally, never as absence.
 */
export type KnownEpisodeKind = 'absence' | 'apron' | 'after_hours' | 'extra_person';
export type EpisodeKind = KnownEpisodeKind | (string & {});
/**
 * A store minute's mode: `open` inside the opening hours; `margin` the rest of the working
 * window (hours widened by the margin, observation only); `closed` outside it (after hours).
 */
export type PresenceMode = 'open' | 'margin' | 'closed';
export interface PresenceEpisode {
  id: string;
  kind: EpisodeKind;
  camera_id: string;
  started_at: string;
  ended_at: string | null;
  ongoing: boolean;
  duration_seconds: number;
  archive_local_start: string | null;
  /** Measured recorder minus store clock, in seconds; null when unknown. */
  clock_offset_s?: number | null;
  /** GoPOS sale during an absence episode; null for any other kind. */
  sale_during: boolean | null;
  /**
   * Absence only: minutes with customers on the sales floor (`floor` zones) and the most
   * seen at once; null for other kinds (and from an older server).
   */
  customers_seen?: { minutes: number; max: number } | null;
  review_state: PresenceReviewState;
  review_comment: string | null;
  reviewed_at: string | null;
  /** The frame that shows the episode best, with counted person boxes; null without one. */
  frame?: EpisodeFrame | null;
  /**
   * After hours: Luna's second opinion on the key frame (latest answer, a recheck wins);
   * null when not asked (every other kind, and from an older server).
   */
  auto_check?: EpisodeAutoCheck | null;
}
/** `no` dismissed the episode as a system review; `error`: the check failed. */
export type AutoCheckVerdict = 'yes' | 'no' | 'unsure' | 'error';
export interface EpisodeAutoCheck {
  verdict: AutoCheckVerdict;
  /** What the model saw in the red rectangle (its own words). */
  what: string | null;
  model: string | null;
  checked_at: string | null;
  /** The answer of a second look after the episode kept growing. */
  recheck: boolean;
}
export interface EpisodeFrame {
  sha256: string;
  camera_id: string;
  captured_at: string | null;
  /** Normalized x1, y1, x2, y2 of each counted person. */
  boxes: [number, number, number, number][];
}
/**
 * Why a check or run got its result: detector `staff_zone` / `work_zone` /
 * `people_outside_zones` / `empty`, a no_data code, or an LLM-era reason.
 */
export type PresenceReason = string;
export interface PresenceSegment {
  from: string;
  to: string;
  result: PresenceResult;
  /**
   * Visible runs split by reason (`staff_zone` vs `work_zone`); `no_data` runs by their
   * coarse cause (`no_signal`, `zones`, `other`); null otherwise.
   */
  reason?: PresenceReason | null;
  /** Closed-store minutes are never in segments. */
  mode: Exclude<PresenceMode, 'closed'>;
}
export interface PresenceHours {
  opens: string;
  closes: string;
}
export interface PresenceWorkingWindow {
  from: string;
  to: string;
  opens_at: string;
  closes_at: string;
}
/** First (arrival) or last (departure) visible minute of the working window. */
export interface PresenceVisibleMark {
  at: string;
  /** Detector reason of that minute (`staff_zone`, `work_zone`, `floor_staff`, ...). */
  reason: PresenceReason | null;
}
export interface StorePresence {
  store_id: string;
  hours: PresenceHours | null;
  /**
   * Opening hours widened by the margins, UTC instants; null on a day without hours.
   * `opens_at`/`closes_at` are the opening hours inside it: the margin before opening
   * can differ per store (`margin_before_minutes`), so never derive them from the window.
   */
  working_window: PresenceWorkingWindow | null;
  arrival: PresenceVisibleMark | null;
  departure: PresenceVisibleMark | null;
  threshold_minutes: number;
  /** Persons the store normally has behind the counter (extra person rule). */
  expected_staff?: number;
  /** The latest fresh working-window minute; closed-store minutes never appear here. */
  current: {
    result: PresenceResult;
    reason?: PresenceReason | null;
    /** Apron state of that minute; null when nobody was in a staff zone or unscored. */
    apron?: ApronState | null;
    mode: Exclude<PresenceMode, 'closed'>;
    observed_at: string;
    age_seconds: number;
  } | null;
  segments: PresenceSegment[];
  episodes: PresenceEpisode[];
  /** Close instants of the day's completed GoPOS sales at the store (up to now). */
  sales: string[];
  /** Recorder clock minus store clock over the day (s, median of the checks); null unknown. */
  clock_offset_s?: number | null;
}
export interface PresenceDay {
  day: string;
  timezone: string | null;
  configured: boolean;
  stores: StorePresence[];
}
/** One kind's episodes of a store day: `count` and `seconds` leave dismissed ones out. */
export interface PresenceKindTotals {
  count: number;
  seconds: number;
  unreviewed: number;
}
/** A store's local day in the period summary. */
export interface PresenceSummaryDay {
  day: string;
  /** Null: closed all day. */
  hours: PresenceHours | null;
  arrival: PresenceVisibleMark | null;
  departure: PresenceVisibleMark | null;
  /** Minutes the arrival came after the opening; null without hours or arrival. */
  late_minutes: number | null;
  /** No usable camera observation that day (nor an arrival or episode). */
  no_data: boolean;
  /** By kind: `absence`, `apron`, `after_hours`, `extra_person` always, a newer kind when it occurs. */
  episodes: Record<string, PresenceKindTotals>;
}
export interface PresenceSummary {
  from: string;
  to: string;
  timezone: string | null;
  configured: boolean;
  stores: { store_id: string; days: PresenceSummaryDay[] }[];
}
/** An episode of a period: the day view's shape, its store and local start day. */
export interface PeriodEpisode extends PresenceEpisode {
  store_id: string;
  day: string;
}
export interface PresenceEpisodes {
  from: string;
  to: string;
  status: 'unreviewed' | 'all';
  timezone: string | null;
  configured: boolean;
  episodes: PeriodEpisode[];
}
export type PresenceRole = 'staff_context' | 'customer_context' | 'unknown';
export interface PresencePerson {
  /** Normalized x1, y1, x2, y2 within 0..1. */
  bbox: [number, number, number, number];
  visibility: 'full' | 'partial' | string;
  role: PresenceRole;
  evidence: string;
}
export interface PresenceFrame {
  sha256: string;
  width: number;
  height: number;
  /** False once retention deleted the file. */
  available: boolean;
}
/**
 * One camera's check within an observation; null fields mean the model was not asked.
 * `skipped`: captured with the other cameras, not classified because an earlier camera
 * already showed staff (`reason` = `visible_on_other_camera`).
 */
export interface PresenceCameraCheck {
  position: number;
  camera_id: string;
  label: PresenceResult | 'skipped';
  reason: string | null;
  people_count: number | null;
  people: PresencePerson[] | null;
  quality: 'good' | 'poor' | string | null;
  ambiguous_fragment: boolean | null;
  error_code: string | null;
  captured_at: string | null;
  frame: PresenceFrame | null;
  /** Model requests for this frame (retries, fallback, second opinion); null when not asked. */
  attempts: number | null;
  /** Model whose answer is `label`; null without an answer. */
  answered_by_model: string | null;
  fallback_used: boolean;
  /** The fallback model's label when asked about an uncertain primary answer. */
  second_opinion_label: PresenceResult | null;
  /** Model or detector id that produced the check (`deim-d-fine-l@77742e19`); null without one. */
  model: string | null;
  /** Local detector answer with its boxes and zone hits; null for LLM checks and failures. */
  detection: PresenceDetection | null;
}
/** Zone kinds this page knows (rule `zones-v5`). */
export type ZoneKind = 'staff' | 'work' | 'floor' | 'aisle' | 'goods' | 'ignore';
/** A stored kind: a newer server may send one this page does not know yet. */
export type ZoneKindCode = ZoneKind | (string & {});
export type Point = [number, number];
export interface PresenceZone {
  /** `^[a-z0-9][a-z0-9-]{0,31}$`, unique per camera. */
  id: string;
  name: string;
  kind: ZoneKindCode;
  /** Normalized [0, 1] points of the full frame. */
  polygon: Point[];
}
/** One person box the detector found (score at or above the rule's minimum). */
export interface DetectedPerson {
  /** Normalized x1, y1, x2, y2. */
  box: [number, number, number, number];
  score: number;
  counted: boolean;
  ignored: boolean;
  zone_id: string | null;
  zone_kind: ZoneKindCode | null;
  /** Zone id -> share of the box inside it. */
  overlaps: Record<string, number>;
  /** Seller-uniform score (logit) of a staff-, floor- or aisle-zone person; absent when not scored. */
  uniform_score?: number | null;
  /** After hours: a box that stood still, not counted (`after-hours-v2`/`v3`: weak ones only; `v4`: any score). */
  stationary?: boolean;
}
export interface ZoneHit {
  zone_id: string;
  kind: ZoneKindCode;
  persons: number;
  best_score: number;
}
/** Seller-uniform thresholds (logits) of the classifier head. */
export interface UniformThresholds {
  /** A floor person at or above it wears the uniform (counts as staff). */
  staff_threshold: number;
  /**
   * The same for an aisle (mall corridor) person, stricter (uniform-v2); absent on checks
   * stored before it, which were judged by `staff_threshold`.
   */
  aisle_threshold?: number;
  /** A staff-zone person at or above it wears the apron. */
  apron_worn: number;
  /** Every staff-zone person below it (one box tall enough): the apron is missing. */
  apron_missing: number;
  /** Share of the frame height a box needs for `missing`. */
  apron_min_height: number;
}
/** The uniform classifier's part of a stored check (zones-v2). */
export interface CheckUniform extends UniformThresholds {
  model: string;
  /** `apron`: staff-zone persons scored for the apron; `floor`: floor persons for the uniform. */
  role: 'apron' | 'floor' | (string & {});
  apron?: ApronState | null;
  /** Classifier error code (`uniform_not_configured`, ...); null when it ran. */
  error: string | null;
}
/** Detector answer stored with a check. */
export interface PresenceDetection {
  /** `zones-v2`..`zones-v5` in the working window; `after-hours-v1`..`v5` in the closed store. */
  rule: string;
  /** Index of the minute's sample whose frame this is (24/7 sampling). */
  sample?: number;
  min_score: number;
  /** zones-v3: staff/work zones count a person from this score. */
  staff_min_score?: number;
  /** after-hours-v2/v3: a box this sure counts even when it stands still; v4: a box this sure next to a new track is an arrival that protects it. */
  night_sure_score?: number;
  min_overlap: number;
  ignore_overlap: number;
  label: 'visible' | 'not_visible' | 'uncertain' | 'no_data';
  reason: PresenceReason;
  detections: DetectedPerson[];
  /** Weaker boxes that were counted but not listed. */
  below_min_score?: number;
  zone_hits: ZoneHit[];
  model?: string;
  zones_version?: number;
  uniform?: CheckUniform | null;
}
export interface CameraZones {
  camera_id: string;
  position: number;
  /** 0 while the camera has no stored zones; sent back unchanged with a save. */
  version: number;
  zones: PresenceZone[];
  /** False when the stored row no longer passes validation. */
  valid: boolean;
  has_staff_zone: boolean;
  updated_by: string | null;
  updated_at: string | null;
}
export interface ZoneRule {
  version: string;
  min_score: number;
  /** zones-v3: a person counts in a staff or work zone from this score. */
  staff_min_score: number;
  min_overlap: number;
  ignore_overlap: number;
  max_zones: number;
  min_points: number;
  max_points: number;
  kinds: ZoneKindCode[];
}
export interface StoreZones {
  configured: boolean;
  store_id: string | null;
  cameras: CameraZones[];
  rule: ZoneRule;
}
/** The uniform classifier on a zone preview frame. */
export interface ZonePreviewUniform extends UniformThresholds {
  model: string;
  /** Set when a staff-zone person was scored. */
  apron: ApronState | null;
  /** Classifier error code; null when it ran (or had nobody to score). */
  error: string | null;
  inference_ms: number;
}
export interface ZonePreview {
  camera_id: string;
  label: 'visible' | 'not_visible' | 'uncertain' | 'no_data';
  reason: PresenceReason;
  people_count: number;
  model: string;
  rule: string;
  min_score: number;
  min_overlap: number;
  ignore_overlap: number;
  inference_ms: number;
  detections: DetectedPerson[];
  zone_hits: ZoneHit[];
  /** Null when the detector gave no verdict (no staff zone, no data). */
  uniform: ZonePreviewUniform | null;
  frame: {
    source: 'snapshot' | 'stored';
    sha256: string;
    width: number;
    height: number;
    captured_at: string | null;
    /** Fresh snapshot only: the analysed JPEG (at most 1280 px wide), base64. */
    image_base64: string | null;
  };
}
/**
 * One 20 s sample of a store minute (every camera captured at once). In a closed-store
 * minute `result` is visible when a person was seen.
 */
export interface PresenceSample {
  index: number;
  captured_at: string | null;
  result: PresenceResult;
  reason: PresenceReason | null;
  apron: ApronState | null;
  /** Most people one camera counted; null without an answer. */
  people: number | null;
  /** Most people one camera counted in staff/work zones (detector only). */
  staff?: number;
  /** The sample whose frames were stored (the one that decided the minute). */
  chosen: boolean;
  /** Camera id -> strongest counted person's detector score, when one was counted. */
  best_person_score?: Record<string, number>;
}
export interface PresenceCheck {
  /** Recorder clock minus store clock, measured for this observation. */
  clock_offset_s?: number | null;
  observation_id: number;
  observed_at: string;
  mode: PresenceMode;
  /** The minute's samples; null for minutes decided from a single check (before 24/7). */
  samples: PresenceSample[] | null;
  result: PresenceResult;
  /** Why the store got `result` (detector reasons; null for older checks). */
  reason?: PresenceReason | null;
  /** Apron state of the minute; null when not assessed. */
  apron?: ApronState | null;
  /** Persons behind the counter (staff/work zones) seen in two samples; null unknown. */
  staff_count?: number | null;
  cameras: PresenceCameraCheck[];
  /** Seconds between the first and last camera capture of this check. */
  capture_spread_s: number | null;
}
export interface PresenceChecks {
  configured: boolean;
  checks: PresenceCheck[];
}
export interface PresenceReceiptItem {
  name: string;
  /** Decimal string («2», «0.35»); null when GoPOS sent none. */
  quantity: string | null;
  /** Decimal string in PLN. */
  total: string | null;
}
/** A completed GoPOS sale of the store's day (the timeline's sales ticks). */
export interface PresenceReceipt {
  id: string;
  closed_at: string;
  number: string | null;
  /** Decimal string in PLN. */
  total: string | null;
  /** From the reconciled payment legs; null when unknown. */
  payment: 'cash' | 'card' | 'mixed' | 'other' | null;
  employee: string | null;
  items: PresenceReceiptItem[];
}
export interface PresenceReceipts {
  configured: boolean;
  /** The camera a receipt opens on: the first with a zone behind the counter. */
  till_camera: string | null;
  receipts: PresenceReceipt[];
}
export interface EpisodeReview {
  state: 'confirmed' | 'dismissed';
  comment?: string;
}
/** Presence reports of a camera grant: none (video only), view, review (also decisions). */
export type PresenceAccess = 'none' | 'view' | 'review';
/** What the signed-in user may see of cameras (MD-0086 addendum 2026-09-28). */
export interface CameraAccess {
  can_view: boolean;
  presence: PresenceAccess;
}
/** A non-admin user's camera grant (administrators need none). */
export interface CameraGrant {
  user_id: string;
  username: string;
  display_name: string;
  presence: PresenceAccess;
  granted_at: string;
}
export const cameraKeys = {
  access: () => ['camera-access'] as const,
  grants: () => ['camera-grants'] as const,
};
export const camerasApi = {
  access: () => api.webAdmin.get<CameraAccess>('/cameras/access'),
  grants: () => api.webAdmin.get<{ grants: CameraGrant[] }>('/cameras/grants'),
  setGrant: (userId: string, presence: PresenceAccess) =>
    api.webAdmin.put<void>(`/cameras/grants/${userId}`, { presence }),
  revokeGrant: (userId: string) => api.webAdmin.delete<void>(`/cameras/grants/${userId}`),
  catalog: (signal?: AbortSignal) =>
    api.webAdmin.get<CameraCatalog>('/cameras/catalog', { signal: signal ?? null }),
  recordings: (camera: string, day: string, signal?: AbortSignal) =>
    api.webAdmin.get<{ intervals: RecordingInterval[] }>(`/cameras/recordings/${camera}`, {
      params: { day },
      signal: signal ?? null,
    }),
  /** `keepalive`: sent while the page unloads (its lease released). */
  leaseArchive: (
    viewer: string,
    lease: ArchiveLeaseRequest,
    options: { signal?: AbortSignal; keepalive?: boolean } = {},
  ) =>
    api.webAdmin.post<ArchiveLease>(`/cameras/archive/viewers/${viewer}`, lease, {
      signal: options.signal ?? null,
      // A renewal repeats the same lease: nothing to deduplicate.
      autoIdempotencyKey: false,
      ...(options.keepalive ? { keepalive: true, transientRetry: false } : {}),
    }),
  presenceDay: (day: string, store?: string, signal?: AbortSignal) =>
    api.webAdmin.get<PresenceDay>('/camera-presence/day', {
      params: { day, store },
      signal: signal ?? null,
    }),
  /** Per store and local day of [from, to] (`YYYY-MM-DD`, at most 62 days). */
  presenceSummary: (from: string, to: string, signal?: AbortSignal) =>
    api.webAdmin.get<PresenceSummary>('/camera-presence/summary', {
      params: { from, to },
      signal: signal ?? null,
    }),
  /** Episodes started on the local days [from, to] (at most 62 days). */
  presenceEpisodes: (
    from: string,
    to: string,
    status: 'unreviewed' | 'all',
    signal?: AbortSignal,
  ) =>
    api.webAdmin.get<PresenceEpisodes>('/camera-presence/episodes', {
      params: { from, to, status },
      signal: signal ?? null,
    }),
  /** Checks in [from, to): ISO instants with a timezone, at most 3 hours apart. */
  presenceChecks: (store: string, from: string, to: string, signal?: AbortSignal) =>
    api.webAdmin.get<PresenceChecks>('/camera-presence/checks', {
      params: { store, from, to },
      signal: signal ?? null,
    }),
  /**
   * A sheet of small pictures of a recorded camera (the gateway's thumbnails): `cadence`
   * seconds between pictures, `start` the sheet's first second (UTC epoch). The header
   * `X-Camera-Tiles` names the slots the image holds.
   */
  thumbSheet: (camera: string, cadence: number, start: number, signal?: AbortSignal) =>
    api.webAdmin.blob(`/cameras/thumbs/${camera}/${cadence}/${start}`, {
      signal: signal ?? null,
    }),
  /** The store's completed sales of `day` up to now, with totals and items. */
  presenceReceipts: (store: string, day: string, signal?: AbortSignal) =>
    api.webAdmin.get<PresenceReceipts>('/camera-presence/receipts', {
      params: { store, day },
      signal: signal ?? null,
    }),
  reviewEpisode: (id: string, body: EpisodeReview) =>
    api.webAdmin.post<PresenceEpisode>(`/camera-presence/episodes/${id}/review`, body),
  storeZones: (store: string, signal?: AbortSignal) =>
    api.webAdmin.get<StoreZones>('/camera-presence/zones', {
      params: { store },
      signal: signal ?? null,
    }),
  /** Replaces the camera's zones; 409 `presence_zones_conflict` when `version` is stale. */
  saveZones: (camera: string, version: number, zones: PresenceZone[]) =>
    api.webAdmin.put<CameraZones>(`/camera-presence/zones/${camera}`, { version, zones }),
  /** A fresh full-resolution snapshot of the camera (not stored). */
  zoneFrame: (camera: string, signal?: AbortSignal) =>
    api.webAdmin.blob(`/camera-presence/zones/${camera}/frame`, { signal: signal ?? null }),
  /** The detector with a zone draft on a fresh snapshot, or on a stored check frame. */
  previewZones: (
    camera: string,
    zones: PresenceZone[],
    frameSha256: string | null,
    signal?: AbortSignal,
  ) =>
    api.webAdmin.post<ZonePreview>(
      `/camera-presence/zones/${camera}/preview`,
      frameSha256 ? { zones, frame_sha256: frameSha256 } : { zones },
      { signal: signal ?? null },
    ),
};
export const liveSource = (id: string, quality: 'grid' | 'hd') =>
  `/api/web-admin/cameras/live/${id}/${quality}/index.m3u8`;
/** An archive session's stream (HLS), playable from its first segment while it loads. */
export const archiveStreamSource = (id: string, stamp: string, quality: ArchiveQuality) =>
  `/api/web-admin/cameras/archive/${id}/${stamp}/${quality}/index.m3u8`;
/** A stored presence frame; `width` asks for the server-resized JPEG (thumbnails). */
export const presenceFrameSource = (sha256: string, width?: 320 | 640) =>
  `/api/web-admin/camera-presence/frames/${sha256}${width ? `?w=${width}` : ''}`;
/** `YYYYMMDDTHHMMSS` of a device-local minute or moment (`YYYY-MM-DDTHH:MM[:SS]`). */
export const archiveStamp = (local: string) => {
  const compact = local.replace(/[-:]/g, '');
  return compact.length >= 15 ? compact.slice(0, 15) : compact.slice(0, 13) + '00';
};

/** Camera grants for the users page (administrators only). */
export function useCameraGrants({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cameraKeys.grants(),
    queryFn: async () => (await camerasApi.grants()).grants,
    enabled,
  });
}

/** `off` removes the grant; a presence level creates it (video) or changes the level. */
export type CameraAccessLevel = 'off' | PresenceAccess;

export function useSetCameraAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, level }: { userId: string; level: CameraAccessLevel }) =>
      level === 'off' ? camerasApi.revokeGrant(userId) : camerasApi.setGrant(userId, level),
    onSettled: () => queryClient.invalidateQueries({ queryKey: cameraKeys.grants() }),
  });
}
